import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useAudios } from '@dyl/systems/AudiosAssetsManager';
import AudioMiniPreview from '@dyl/components/AudioMiniPreview';
import { property } from 'lodash';
import useStyles from './styles';
import DataGridDyl from '@dyl/components/DataGridDyl';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import { updateAudioTranscript as apiUpdateTranscript } from '@dyl/apiClients/backendApi';
import { useSnackbar } from 'notistack';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { Edit as EditIcon, Refresh as RetryIcon } from '@mui/icons-material';
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import deleteAssetAudio from "@dyl/apiClients/backendApi/deleteAssetAudio";
import useLogic from './logic';
import { CollapsibleButton } from '@dyl/components/CollapsibleButtonGroup';
import { CollapsibleAssetButtonGroup } from '@dyl/components/CollapsibleButtonGroup';
import SearchField from '@dyl/components/SearchField';
const getRowId = property('audio_id');

/**
 * @param type: audio type: 'ITEM' | 'MOTIVATIONAL'
 * @param {Parameters<DataGridDyl>[0]} props
 */
export default function AudiosList({ type, ...props }) {
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const debounceTimeout = useRef();

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            setDebouncedSearch(searchValue);
        }, 400);
        return () => clearTimeout(debounceTimeout.current);
    }, [searchValue]);
    const [sortModel, setSortModel] = useState([{ field: 'audio_id', sort: 'asc' }]);
    const { audios, countFiltered, audiosStatus, refreshAudios } = useAudios(page, debouncedSearch, type, pageSize, sortModel);
    const [isUpdatingTranscript, setUpdatingTranscript] = useState(false);
    const {
        selectedIds, setSelectedIds,
        selectAll,
        isExporting, onExportClick,
    } = useLogic('assets_audio');

    const handlePageChange = (newPage) => {
    setPage(newPage);
    };

    const updateTranscript = useCallback(async (id, transcript) => {
        while (true) {
            setUpdatingTranscript(true);
            try {
                await apiUpdateTranscript(id, transcript);
                break;
            } catch {
                await new Promise(resolve => {
                    enqueueSnackbar(`Failed to update transcript for ${id}`, {
                        variant: 'error',
                        action: createSnackbarAction({
                            iconComponent: RetryIcon,
                            onClick: resolve,
                        }),
                        persist: true,
                    });
                });
            } finally {
                setUpdatingTranscript(false);
            }
        }
        enqueueSnackbar(`Successfully updated transcript for ${id}`, {
            variant: 'success',
        });
        refreshAudios();
    }, [refreshAudios, enqueueSnackbar]);

    const columns = useMemo(() => [
        {
            headerName: 'Name', field: 'audio_id', flex: 2,
            renderCell({ value }) {
                return (
                    <>
                        <AudioMiniPreview audioId={value} />
                        {value}
                    </>
                );
            },
        },
        {
            headerName: 'Transcript', field: 'transcript', flex: 3,
            editable: true,
            renderCell({ value, api, id, field }) {
                const beginEdit = () => {
                    api.setCellMode(id, field, 'edit');
                };
                if (value) return <>
                    {value}
                    <IconButton onClick={beginEdit} size="large">
                        <EditIcon fontSize='small' />
                    </IconButton>
                </>;
                return (
                    <Button color='primary' onClick={beginEdit}>
                        Add Transcript
                    </Button>
                )
            }
        },
        {
            headerName: 'Updated Date/Time', field: 'updated_at', flex: 2,
            valueFormatter({ value }) {
                return quickDateFormat(value);
            }
        },
        {
            headerName: 'Delete', field: 'ADMIN_DELETE', flex: 2,
            renderCell({ row }) {
                return (<GenericDeleteAction
                    data={row}
                    promptTexts={{
                        dialogTitle: () => 'Delete Audio?',
                        dialogText: (data) => `Are you sure you want to delete audio "${data.audio_id}"?`,
                        ongoingNotification: (data) => `Deleting audio "${data.audio_id}"...`,
                        successNotification: (data, response) => `Deleted audio "${data.audio_id}".`,
                        failureNotification: (data, response) =>
                            `The audio file you are trying to delete is associated with the following task(s): ` +
                            `${response.associated_tasks.map(x => x.task_id_name).join(', ')}, please remove the asset from the associated task(s) in order to delete it.`,
                        errorNotification: () => `Failed to delete audio.`
                    }}
                    deleteAction={(data) => deleteAssetAudio(data.audio_id)}
                    responseSuccessPredicate={(response) => response.success}
                    onDelete={() => refreshAudios()} />)
            }
        },
    ], [refreshAudios]);

    /** @type {Parameters<DataGridDyl>[0]['onCellEditCommit']} */
    const onCellEditCommit = useCallback(({ id, field, value }) => {
        switch (field) {
            case 'transcript': {
                const old = audios.find(a => a.audio_id === id)?.transcript;
                if ((!old && !value) || old === value) return;
                updateTranscript(id, value);
                break;
            }
            default:
                throw new Error(`Editing field ${field} is not supported.`);
        }
    }, [audios, updateTranscript]);

        return (
            <>
            <SearchField
            value={searchValue}
            onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
            }}
            placeholder="Search..."
            />
            <Box className={classes.actionLine}>
                <Typography variant='h6'>
                        Showing {audios.length} of {countFiltered} records.
                        </Typography>
                            <CollapsibleAssetButtonGroup className={classes.actionGroup} color='primary'>
                                <CollapsibleButton down='md' onClick={() => onExportClick(selectedIds)} disabled={isExporting}>
                                    Export Asset(s)
                                </CollapsibleButton>
                            </CollapsibleAssetButtonGroup>
                        </Box>
            <DataGridDyl
            rows={audios}
            getRowId={getRowId}
            columns={columns}
            loading={audiosStatus !== 'complete' || isUpdatingTranscript}
            onCellEditCommit={onCellEditCommit}
            keepNonExistentRowsSelected={true}

            selectionType='checkboxes'
            selectionModel={selectedIds}
            onSelectionModelChange={(newSelection) => {
                setSelectedIds((prev) => {
                    const currentPageIds = audios.map((aud) => aud.audio_id);
                    const added = newSelection.filter((id) => !prev.includes(id));
                    const removed = prev.filter(
                    (id) => currentPageIds.includes(id) && !newSelection.includes(id)
                    );
                    return [...prev.filter((id) => !removed.includes(id)), ...added];
                });
            }}
            page={page}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowCount={countFiltered}
            paginationMode='server'
            sortingMode='server'
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            overrideSelectAll={selectAll}
            disableColumnMenu={true}
            pagination
            {...props}
        />
        </>
    );
}